/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CopyText,
  getLocaleTimeOffset,
  Job,
  linksDefinition,
  plurals,
  ProcessDescription,
  statusColors
} from 'ogc-processes-components';
import { Card } from 'primereact/card';
import { CardSkeleton } from '@/components/CardSkeleton';
import { ChartNoAxesColumn, GalleryVerticalEnd } from 'lucide-react';
import { NavLink } from 'react-router';
import { Utils } from '@/utils/utils.ts';
import { ButtonGroup } from 'primereact/buttongroup';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DateTime, Interval } from 'luxon';
import { Knob } from 'primereact/knob';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { ExecutionContent } from './ExecutionContent';
import { JSONPath } from 'jsonpath-plus';
import { AntFlowJob } from '@/types/models';
import { AppRoutes } from '@/constants/routes';
import { useAuthService } from '@/hooks/useAuthService';

interface Props {
  owner?: {
    id: string;
    email: string;
  };
  className?: string;
  job: AntFlowJob;
  process?: ProcessDescription;
  processIsLoading?: boolean;
  onRetryJob?: () => void;
  handleCancelJob?: () => Promise<Job>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const jobResultUrl = (job: Job) => {
  const base = 'https://radiantearth.github.io/stac-browser/#/external/';
  const catalog_links = job.links.find((link) => link.rel === linksDefinition.stacBrowser)?.href;
  if (catalog_links) {
    return catalog_links;
  }
  const { result_url } = job;
  if (result_url) {
    const url = new URL(result_url);
    const { pathname, host } = url;
    return base.concat(host).concat(pathname);
  }
};

export const JobGeneralMetaCard: React.FC<Props> = (props) => {
  const { t, i18n } = useTranslation();
  const toast = useRef<Toast>(null);
  const { className, job, process, processIsLoading, onRetryJob, handleCancelJob, owner } = props;
  const { injectedToken } = useAuthService();

  const openOGCAPIMutation = useMutation({
    mutationFn: async () => {
      const link = job.links.find((el) => el.rel === linksDefinition.self);
      if (!link?.href) {
        return;
      }
      const url = await injectedToken(link.href);
      window.open(url, '__blank');
      return;
    }
  });

  const getDuration = () => {
    let duration = undefined;
    if (job.finished) {
      const created = DateTime.fromISO(job.created);
      const finished = DateTime.fromISO(job.finished);
      const interval = Interval.fromDateTimes(created, finished);
      duration =
        interval.length('seconds') <= 60
          ? `~ ${plurals(Math.ceil(interval.length('seconds')), 'sec')}.`
          : interval.length('minutes') <= 60
            ? `~ ${plurals(Math.ceil(interval.length('minutes')), 'min')}.`
            : ` ~ ${Math.ceil(interval.length('hours'))} h`;
    }
    return duration;
  };

  const [offsetMinute] = getLocaleTimeOffset();

  const IsOwnerJob = useMemo(() => {
    return job.user_id === owner?.id;
  }, [job, owner]);

  const process_link = useMemo(() => {
    if (!job.links) {
      return '#';
    }
    const link = job.links.find((el) => el.rel === linksDefinition.process);
    if (!link) {
      return '#';
    }
    const { project_path, project_ref } = job;
    let browserProjectUrl = `/projects`;
    if (project_path && project_ref) {
      browserProjectUrl =
        `/projects/${project_path}/ref/${project_ref}/project` +
        Utils.buildNewQueryParamsUrl({
          tabs: '1',
          process_global: job.processID
        });
    }
    return AppRoutes.projects.detail.url(browserProjectUrl);
  }, [job]);

  const resultsUrl = useMemo(() => {
    return jobResultUrl(job);
  }, [job]);

  const cwlProviderLink = useMemo(() => {
    const sourceLink = process?.links?.find((link) => link.rel === linksDefinition.provider);
    if (sourceLink) {
      return sourceLink.href;
    }
  }, [process]);

  const isDynaJob = useMemo(() => {
    return process?.id.startsWith('dyna~');
  }, [process]);

  const cwlMandatoryRequestedFlavor = useMemo(() => {
    let userRequestFlavor: string[] = JSONPath({
      path: '$.antflow_config:requested_flavors',
      json: job.execution_content!
    })[0];
    const _cwlMandatoryRequestedFlavor: string[] = [];
    userRequestFlavor = userRequestFlavor ?? [];
    job.requested_flavors?.forEach((flavor) => {
      if (userRequestFlavor && !userRequestFlavor.includes(flavor)) {
        _cwlMandatoryRequestedFlavor.push(flavor);
      }
    });
    return _cwlMandatoryRequestedFlavor.length > 0
      ? [{ name: 'CWL flavors', value: JSON.stringify(_cwlMandatoryRequestedFlavor) }]
      : undefined;
  }, [job]);

  return (
    <div className={classNames(className)}>
      <Toast ref={toast} />
      {/* Card header*/}
      <div className="flex xl:flex-row xl:align-items-center xl:justify-content-between  sm:flex-column sm:align-items-start">
        {/* Card title*/}
        <div className="sm:mb-2 flex align-items-center">
          {processIsLoading ? (
            <CardSkeleton variant="text" />
          ) : (
            process && (
              <>
                <GalleryVerticalEnd className="mr-2" size={18} />
                {isDynaJob ? (
                  <span className="text-lg pb-2 inline-block">Dyna process</span>
                ) : (
                  <div className="flex align-items-center inline-block">
                    <NavLink
                      to={`${process_link}`}
                      className="text-lg text-color no-underline hover:underline font-medium"
                    >
                      {process.title ?? job.project_path?.split('/').toReversed()[0]}
                    </NavLink>
                  </div>
                )}
              </>
            )
          )}
        </div>

        {/* Job Actions*/}
        <div className="flex align-items-center">
          <ButtonGroup>
            <Button
              size="small"
              onClick={() => openOGCAPIMutation.mutate()}
              loading={openOGCAPIMutation.isPending}
              label={`${t(['actions.open', 'open'])} OGC API`}
              iconPos="left"
              className={classNames({ 'p-button-ground-rounded': !resultsUrl })}
              icon="pi pi-external-link"
            />
            {job.result_url && resultsUrl && (
              <Button
                severity="secondary"
                size="small"
                onClick={() => window.open(resultsUrl, '__blank')}
                label={t(['actions.show_results', 'Show results'])}
                iconPos="left"
                icon="pi pi-folder-open"
              />
            )}
          </ButtonGroup>
        </div>
      </div>

      {/*  */}
      <div className="mt-3 flex align-items-start justify-content-between flex-wrap">
        {/* Generale info */}
        <Card className="xl:col-6 lg:col-11  sm:col-12">
          <div className="flex aling-end align-items-center justify-content-between">
            <div>
              <ChartNoAxesColumn className="mr-3" />
              <span className="inline-block text-xl font-medium">{t(['misc.general_info', 'General Info'])}</span>
            </div>
            <div
              className="py-2 px-3 border-round mx-3 flex align-items-center"
              style={{
                color: 'var(--primary-color-text)',
                backgroundColor: statusColors[job.status]
              }}
            >
              {job.status === 'successful' && <i className="pi pi-check mr-2" />}
              {job.status === 'accepted' && <i className="pi pi-hourglass mr-2" />}
              {job.status === 'running' && (
                <ProgressSpinner
                  style={{ width: '20px', height: '20px' }}
                  strokeWidth="8"
                  className="mr-2"
                  animationDuration=".5s"
                />
              )}
              {['failed', 'dismissed'].includes(job.status) && <i className="pi pi-times-circle mr-2" />}
              <span className="font-semibold capitalize">
                {t([`jobs.properties.status.${job.status}`, job.status])}
              </span>
            </div>
          </div>

          <div className="flex mt-2 justify-content-between">
            <div className="xl:col-8 lg:col-9  pb-0">
              <div className="">
                <div className="mt-1 mb-2">
                  <CopyText text={job.jobID} iconClassName="text-lg" />
                </div>

                {!IsOwnerJob && job.username && (
                  <div>
                    <span className="inline-block font-semibold">{t(['misc.owner', 'Owner'])}:&nbsp;</span>
                    <span className="inline-block">{job.username}</span>
                  </div>
                )}

                {job.assigned_to && (
                  <div className="flex flex-wrap xl:flex-row lg:flex-row lg:align-items-center  sm:flex-column sm:align-items-start">
                    <span className="inline font-semibold">{t(['misc.assigned_to', 'Assigned to'])}:&nbsp;</span>
                    <span
                      style={{ maxWidth: '65%' }}
                      className="inline white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    >
                      {job.assigned_to}
                    </span>
                  </div>
                )}
                <div className="my-1">
                  <span className="inline-block font-semibold">{t(['date.created_at', 'Started'])}:&nbsp;</span>
                  <span className="inline-block">
                    {DateTime.fromISO(job.created)
                      .plus({ minute: offsetMinute })
                      .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, {
                        locale: i18n.language ?? 'en'
                      })}
                  </span>
                </div>
                {job.finished ? (
                  <div className="my-1">
                    <span className="inline-block font-bold">{t(['date.finished_at', 'Finished'])}:&nbsp;</span>
                    <span className="inline-block">
                      {DateTime.fromISO(job.finished)
                        .plus({ minute: offsetMinute })
                        .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, {
                          locale: i18n.language ?? 'en'
                        })}
                    </span>
                  </div>
                ) : (
                  job.updated && (
                    <div className="my-1">
                      <span className="inline-block font-semibold">
                        {t(['date.updated_at', 'Last updated'])}:&nbsp;
                      </span>
                      <span className="inline-block">
                        {DateTime.fromISO(job.updated)
                          .plus({ minute: offsetMinute })
                          .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, {
                            locale: i18n.language ?? 'en'
                          })}
                      </span>
                    </div>
                  )
                )}
                {getDuration() && (
                  <div className="my-1">
                    <span className="inline-block font-semibold mr-2">{t(['misc.duration', 'Duration'])}:&nbsp;</span>
                    <span className="inline-block">{getDuration()}</span>
                  </div>
                )}

                <div className="flex mt-1">
                  {job.log_urls && job.log_urls.length !== 0 && (
                    <div className="w-5">
                      <span className="inline-block font-semibold">{t(['misc.log_file', 'filed'])}:&nbsp;</span>
                      <span className="inline-block lowercase">
                        {plurals(job.log_urls.length, t(['misc.file', 'file']))}
                      </span>
                    </div>
                  )}
                  {job.result_url && job.asset_urls && job.asset_urls.length > 0 && (
                    <div className="w-5">
                      <span className="inline-block font-semibold">{t(['misc.assets', 'Assets'])}:&nbsp;</span>
                      <a
                        href={resultsUrl}
                        target="_blank"
                        className="hover:text-primary text-color inline-block cursor-pointer lowercase"
                      >
                        {plurals(job.asset_urls.length, t(['misc.ressource', 'resource']))}
                      </a>
                    </div>
                  )}
                </div>

                {!isDynaJob && (
                  <div className="flex xl:flex-row sm:flex-column  xl:align-items-center  sm:flex-wrap flex-wrap">
                    <div className="w-12 mt-1">
                      <span className="font-semibold">Reference:&nbsp;</span>
                      {cwlProviderLink ? (
                        <a target="_blank" className="text-color hover:text-primary" href={cwlProviderLink}>
                          <span>{job.project_ref}</span>
                        </a>
                      ) : (
                        <span>{job.project_ref}</span>
                      )}
                    </div>

                    <div className="w-12 my-1">
                      <span className="font-semibold">Commit:&nbsp;</span>
                      <span className="">{job.commit_ref}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="xl:col-3 lg:col-2 flex justify-content-end">
              <Knob
                valueColor={statusColors[job.status]}
                value={job.progress}
                className="font-bold w-fit align-self-center"
                valueTemplate={'{value}%'}
                readOnly
              />
            </div>
          </div>
          <div className="col-12 text-overflow-clip py-0 my-1	">
            <div className="flex align-items-center mb-0">
              <span className="font-semibold inline-block">Message:</span>
            </div>
            <div className="mt-1">
              <textarea
                rows={isDynaJob ? 4 : 3}
                defaultValue={job.message}
                className="px-2 w-full text-sm border-0 border-round"
              />
            </div>
          </div>
        </Card>

        {/* Execution Content */}
        {processIsLoading ? (
          <CardSkeleton
            variant="square"
            className="xl:col-5 lg:col-11 sm:col-12 xl:ml-5 sm:mt-2 lg:mt-0 md:mt-2"
            style={{ height: '28.5rem' }}
          />
        ) : (
          <Card className="xl:col-5 lg:col-11 sm:col-12 xl:ml-5 sm:mt-2 lg:mt-0 md:mt-2" style={{ height: '28.5rem' }}>
            <ExecutionContent
              onRetryJob={onRetryJob}
              handleCancelJob={handleCancelJob}
              extraExecutionContent={cwlMandatoryRequestedFlavor}
              className="h-full"
              process={process}
              job={job}
            />
          </Card>
        )}
      </div>
    </div>
  );
};
